import { Mail, MessageSquareText, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/** How to reach a person at ClassGuardian (#529), set by an admin in Server settings → Support. */
export interface SupportContact {
  note: string;
  email: string;
  phone: string;
}

/** The support contact, loaded once per page. Null until it arrives (or when an admin left both empty). */
export function useSupportContact(): SupportContact | null {
  const [contact, setContact] = useState<SupportContact | null>(null);
  useEffect(() => {
    api
      .get<{ contact?: SupportContact }>('/api/support/options')
      .then((o) => setContact(o.contact && (o.contact.email || o.contact.phone) ? o.contact : null))
      .catch(() => undefined);
  }, []);
  return contact;
}

/** Digits (and a leading +) for tel: and sms: links. */
const dialable = (phone: string) => phone.replace(/[^\d+]/g, '');

/**
 * "Need immediate support? Call, text or email us": big, tappable ways to reach a person, for when a Help & Feedback
 * request can't wait. Shown at the top of Help & Feedback and from the account menu's Contact us.
 */
export function ContactCard({ contact }: { contact: SupportContact }) {
  const tel = dialable(contact.phone);
  return (
    <section className="card contact-card" aria-label="Contact us">
      <div className="contact-head">
        <span className="contact-badge" aria-hidden>
          <Phone size={20} />
        </span>
        <div>
          <h2>Contact us</h2>
          <p className="muted small">{contact.note}</p>
        </div>
      </div>
      <div className="contact-actions">
        {tel && (
          <a className="contact-action" href={`tel:${tel}`}>
            <Phone size={18} />
            <span>
              <strong>Call</strong>
              <span className="small">{contact.phone}</span>
            </span>
          </a>
        )}
        {tel && (
          <a className="contact-action" href={`sms:${tel}`}>
            <MessageSquareText size={18} />
            <span>
              <strong>Text</strong>
              <span className="small">{contact.phone}</span>
            </span>
          </a>
        )}
        {contact.email && (
          <a className="contact-action" href={`mailto:${contact.email}`}>
            <Mail size={18} />
            <span>
              <strong>Email</strong>
              <span className="small">{contact.email}</span>
            </span>
          </a>
        )}
      </div>
    </section>
  );
}
